import React from "react";
import sanitizeHtml from '@/lib/utils/sanitizeHtml';
import { generateListKey } from '@/lib/utils/keyGenerator';
import { CTAComponent } from "@/store/sagas/types/deviceUnlock";
import { MESSAGE_CONSTANTS } from '@/lib/constants/appConstants';
import ModalComponent from "./CommonComponents/ModalComponent";

const CTARenderer: React.FC<{
    cta: CTAComponent;
    onNavigate?: (destination: string) => void;
    isSubmitting?: boolean;
    isRecaptchaReady?: boolean;
    isRecaptchaEnabled?: boolean;
    isSlowNetwork?: boolean;
    hasInputError?: boolean;
}> = ({
    cta,
    onNavigate,
    isSubmitting,
    isRecaptchaReady = true,
    isRecaptchaEnabled = true,
    isSlowNetwork = false,
    hasInputError = false
}) => {
        const [showPopup, setShowPopup] = React.useState(false);
    const disabledHelpId = React.useId();
        const ctaHref = cta.destination || '#';

        const isSubmitButton = cta.action === 'submit' || cta.action === 'native';
    const isModalTrigger = cta.action === 'modal';
        const isDisabled = (isSubmitting && isSubmitButton) ||
            (isRecaptchaEnabled && isSubmitButton && !isRecaptchaReady) ||
            (hasInputError && isSubmitButton);
        const disabledInstructionText = 'Check Status is unavailable. Complete all required fields and wait for security verification to finish to enable it.';
        const disabledDescription = isDisabled && isSubmitButton ? disabledHelpId : undefined;

        // Determine the button text and loading state with network awareness
        const getButtonText = () => {
            if (isSubmitting) return MESSAGE_CONSTANTS.LOADING.PROCESSING;
            if (isRecaptchaEnabled && isSubmitButton && !isRecaptchaReady) {
                return isSlowNetwork
                    ? MESSAGE_CONSTANTS.LOADING.RECAPTCHA_LOADING_SLOW.replace(' (this may take a moment on slow connections)', '')
                    : MESSAGE_CONSTANTS.LOADING.LOADING_RECAPTCHA;
            }
            return cta.text;
        };

        // Enhanced tooltip text for slow networks
        const getTooltipText = () => {
            if (isRecaptchaEnabled && isSubmitButton && !isRecaptchaReady) {
                return isSlowNetwork
                    ? 'Security verification is loading. This may take longer on slow connections...'
                    : 'Waiting for reCAPTCHA to load...';
            }
            return undefined;
        };

        const showSpinner = isSubmitting || (isRecaptchaEnabled && isSubmitButton && !isRecaptchaReady);

        const ctaClassName = `w-full px-6 py-3 rounded-full font-semibold transition-all duration-200 flex items-center justify-center ${cta.style === 'primary'
            ? `bg-black text-white hover:bg-gray-800 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`
            : `bg-gray-200 text-gray-800 hover:bg-gray-300 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`
            }`;

        const handleClick = (e: React.MouseEvent<HTMLElement>) => {

            if (isDisabled) {
                e.preventDefault();
                return;
            }

            if (cta.action === 'modal') {
                e.preventDefault();
                setShowPopup(true);
                return;
            }

            if (cta.action === 'redirect' && cta.destination && onNavigate) {
                e.preventDefault();
                onNavigate(cta.destination);
                return;
            }

            if(cta.action === 'native' || cta.action === 'submit') {
                const form = e.currentTarget.closest('form');
                if (form) {
                    e.preventDefault();
                    form.requestSubmit();
                }
            }

            // For 'native' and 'submit' actions, let the default form behavior handle it
        };

        // Shared modal content
        const modalContent = cta.modalContent && cta.modalContent.length > 0 ? (
            <div className="text-gray-800 text-sm space-y-4">
                {cta.modalContent.map((content, index) => (
                    <div 
                        key={generateListKey('cta-modal', content, index)}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} 
                    />
                ))}
            </div>
        ) : (
            <p>This is the unlock policy popup. You can put your unlock policy details here.</p>
        );

        return (
            <>
                {/* Show slow network warning for submit buttons when reCAPTCHA is not ready */}
                {isRecaptchaEnabled && isSlowNetwork && cta.action === 'submit' && !isRecaptchaReady && (
                    <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-yellow-800">
                                {MESSAGE_CONSTANTS.LOADING.SLOW_NETWORK_WARNING}
                            </span>
                        </div>
                    </div>
                )}
                
                {cta.type === 'button' && !isModalTrigger ? (
                    <a
                        href={ctaHref}
                        onClick={handleClick}
                        aria-disabled={isDisabled}
                        aria-describedby={disabledDescription}
                        className={ctaClassName}
                        title={getTooltipText()}
                    >
                        {showSpinner && (
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        )}
                        {getButtonText()}
                    </a>
                ) : isModalTrigger ? (
                    <button
                        type="button"
                        onClick={handleClick}
                        className={cta.type === 'button' ? ctaClassName : "text-black-600 underline hover:text-blue-800 transition-colors bg-none border-none cursor-pointer p-0"}
                        aria-haspopup="dialog"
                    >
                        {cta.text}
                    </button>
                ) : (
                    <a
                        href={ctaHref}
                        onClick={handleClick}
                        className="text-black-600 underline hover:text-blue-800 transition-colors bg-none border-none cursor-pointer p-0"
                    >
                        {cta.text}
                    </a>
                )}

                {disabledDescription && (
                    <span id={disabledHelpId} className="sr-only">
                        {disabledInstructionText}
                    </span>
                )}

                <ModalComponent
                    open={showPopup}
                    onClose={() => setShowPopup(false)}
                    title={cta.modalTitle || "Unlock Policy"}
                    maxWidth={'sm'}
                >
                    {modalContent}
                </ModalComponent>
            </>
        );
    };

export default CTARenderer;